// To add a resource type to the list of resources with a revert button:
// - Add yourself to revertableResourceType
// - Add the fields to removableResourceFields
// - Create a function at the bottom of has_default_fields.rb with the logic to decide if
//   the resource has modifications or not.

import {useEffect, useState} from 'react';
import {ResetIcon} from '@shopify/polaris-icons';
import {Button, Modal, Text} from '@shopify/polaris';
import {useI18n} from '@shopify/react-i18n';
import {TranslatableResourceType} from 'types';
import {useQuery, useMutation} from '@shopify/react-graphql';
import {ShopLocale, Market} from 'utilities/use-shop-locales-and-markets/types';

import {cookieBannerGrouping} from '../../../../utils/cookieBannerGrouping';

import HasDefaultFieldsQuery from './graphql/HasDefaultFieldsQuery.graphql';
import RevertTranslationsMutation from './graphql/RevertTranslationsMutation.graphql';

export function revertableResourceType(resourceType: TranslatableResourceType) {
  return [
    TranslatableResourceType.EmailTemplate,
    TranslatableResourceType.CookieBanner,
  ].includes(resourceType);
}

function removableResourceFields(resourceType: TranslatableResourceType) {
  const resourceFields = {
    [TranslatableResourceType.EmailTemplate]: ['title', 'body_html'],
    [TranslatableResourceType.CookieBanner]: Object.keys(cookieBannerGrouping),
  };
  return resourceFields[resourceType as keyof typeof resourceFields] || [];
}

export interface Props {
  resourceId: string;
  resourceType: TranslatableResourceType;
  currentLocale?: ShopLocale;
  currentMarket?: Market;
  isSupportedLocale: boolean;
  isLoading: boolean;
  submitting: boolean;
  refetchTranslatableResource(): Promise<void>;
}

export const RevertNotificationModal = ({
  resourceId,
  resourceType,
  currentLocale,
  currentMarket,
  isSupportedLocale,
  submitting,
  isLoading,
  refetchTranslatableResource,
}: Props) => {
  const [i18n] = useI18n();
  const [open, setOpen] = useState(false);
  const [reverting, setReverting] = useState(false);
  const revertTranslations = useMutation(RevertTranslationsMutation);
  const {data, loading, refetch} = useQuery(HasDefaultFieldsQuery, {
    variables: {
      translatableResourceId: resourceId,
      locale: currentLocale?.locale || '',
    },
    skip:
      isLoading ||
      reverting ||
      !revertableResourceType(resourceType) ||
      !currentLocale ||
      !isSupportedLocale ||
      resourceId === '',
  });

  useEffect(() => {
    if (submitting) return;
    // Need to refetch to get the latest data for hasDefaultBody and hasDefaultTitle
    // because the form fields were updated
    refetch();
  }, [refetch, submitting]);

  const hasDefaultFields =
    data?.hasDefaultFields === undefined ? true : data.hasDefaultFields;
  if (
    !revertableResourceType(resourceType) ||
    currentMarket ||
    !currentLocale ||
    !isSupportedLocale
  ) {
    return null;
  }

  return (
    <>
      <Modal
        open={open}
        activator={
          <Button
            icon={ResetIcon}
            onClick={() => setOpen(true)}
            disabled={loading || loading || hasDefaultFields}
          >
            {i18n.translate('RevertNotificationModal.revert')}
          </Button>
        }
        title={i18n.translate('RevertNotificationModal.modal.title', {
          language: currentLocale?.name || '',
        })}
        onClose={() => {
          if (reverting) return;
          setOpen(false);
        }}
        primaryAction={{
          content: i18n.translate('RevertNotificationModal.revert'),
          onAction: async () => {
            setReverting(true);
            await revertTranslations({
              variables: {
                id: resourceId,
                translationKeys: removableResourceFields(resourceType),
                locales: [currentLocale?.locale || ''],
              },
            });
            await refetchTranslatableResource();
            // Refetch the email template data
            await refetch();
            setReverting(false);
            setOpen(false);
          },
          destructive: true,
          disabled: reverting,
        }}
        secondaryActions={[
          {
            content: i18n.translate('Common.cancel'),
            onAction: () => setOpen(false),
            disabled: reverting,
          },
        ]}
      >
        <Modal.Section>
          <Text variant="bodyMd" tone="subdued" as="span">
            {i18n.translate('RevertNotificationModal.modal.description', {
              language: currentLocale?.name || '',
            })}
          </Text>
        </Modal.Section>
      </Modal>
    </>
  );
};
